/**
 * NotifPrefsPanel — Panel para configurar notificaciones de un cliente.
 * Reutilizable desde:
 *   - Admin (ficha cliente): puede editar directamente y aprobar/rechazar pending.
 *   - Portal cliente: solo puede sugerir cambios (quedan pending para admin).
 *
 * Props:
 *   - clienteId
 *   - mode: "admin" | "portal"
 *   - onClose?: callback al cerrar (Dialog parent)
 */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useState } from "react";
import defaultApi from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Bell, Mail, MessageCircle, CheckCircle2, XCircle, Loader2, AlertCircle, Send } from "lucide-react";

export default function NotifPrefsPanel({ clienteId, mode = "admin", onClose, apiClient }) {
  const api = apiClient || defaultApi;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState(null);
  const [note, setNote] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const endpoint = mode === "portal" ? "/portal-sub/notif-prefs" : `/clientes/${clienteId}/notif-prefs`;
      const { data: r } = await api.get(endpoint);
      setData(r);
      setDraft({
        canales: structuredClone(r.prefs.canales),
        resumen_diario: r.prefs.resumen_diario,
        hora_resumen: r.prefs.hora_resumen || "19:00",
        estampar_fotos: r.prefs.estampar_fotos !== false,
      });
    } catch (err) {
      toast.error("Error cargando preferencias");
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-line */ }, [clienteId, mode]);

  const toggleCanal = (evKey, canal) => {
    setDraft((d) => ({
      ...d,
      canales: { ...d.canales, [evKey]: { ...d.canales[evKey], [canal]: !d.canales[evKey][canal] } },
    }));
  };

  const save = async () => {
    setSaving(true);
    try {
      if (mode === "portal") {
        await api.post("/portal-sub/notif-prefs/sugerir", { ...draft, note });
        toast.success("Sugerencia enviada. Pendiente de validación por DNF.");
      } else {
        await api.patch(`/clientes/${clienteId}/notif-prefs`, draft);
        toast.success("Preferencias guardadas");
      }
      await load();
      setNote("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardando");
    } finally { setSaving(false); }
  };

  const aprobarPending = async () => {
    try {
      await api.post(`/clientes/${clienteId}/notif-prefs/aprobar`);
      toast.success("Cambios aprobados y aplicados");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  const rechazarPending = async () => {
    try {
      await api.post(`/clientes/${clienteId}/notif-prefs/rechazar`);
      toast.success("Sugerencia rechazada");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-zinc-500" /></div>;
  }

  const pending = data.prefs.pending_changes;
  const events = data.events;
  const isAdmin = mode === "admin";

  return (
    <div className="space-y-4" data-testid="notif-prefs-panel">
      <div className="flex items-center gap-2">
        <Bell className="w-5 h-5 text-amber-400" />
        <h2 className="text-lg font-semibold text-zinc-100">
          Notificaciones · {data.cliente_nombre}
        </h2>
      </div>

      {/* Pending changes — solo admin lo ve y puede actuar */}
      {pending && isAdmin && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 space-y-2" data-testid="pending-banner">
          <div className="flex items-center gap-2 text-amber-200 text-sm font-semibold">
            <AlertCircle className="w-4 h-4" /> Sugerencia pendiente de revisión
          </div>
          <div className="text-xs text-amber-100/80">
            <div>De: <b>{pending.submitted_name || pending.submitted_by}</b></div>
            <div>Fecha: {new Date(pending.submitted_at).toLocaleString("es-ES")}</div>
            {pending.note && <div className="mt-1 italic">«{pending.note}»</div>}
          </div>
          <div className="text-xs text-amber-100 mt-2">
            <div className="font-mono whitespace-pre-wrap bg-amber-950/40 p-2 rounded border border-amber-500/30 max-h-32 overflow-y-auto">
              {JSON.stringify(pending.changes, null, 2)}
            </div>
          </div>
          <div className="flex gap-2 mt-1">
            <Button onClick={aprobarPending} size="sm"
              className="bg-emerald-500 text-zinc-950 hover:bg-emerald-400"
              data-testid="aprobar-pending-btn">
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Aceptar
            </Button>
            <Button onClick={rechazarPending} size="sm" variant="outline"
              className="border-red-500/40 text-red-300 hover:bg-red-500/10"
              data-testid="rechazar-pending-btn">
              <XCircle className="w-3.5 h-3.5 mr-1" /> Descartar
            </Button>
          </div>
        </div>
      )}

      {/* Si portal y hay pending: aviso */}
      {pending && !isAdmin && (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-100">
          <AlertCircle className="w-4 h-4 inline mr-1" />
          Tienes una sugerencia <b>pendiente de revisión</b> por DNF. Enviada el {new Date(pending.submitted_at).toLocaleDateString("es-ES")}.
        </div>
      )}

      {/* Timbrado de fotos de reportes (solo admin en ficha cliente) */}
      {isAdmin && (
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-zinc-100">📸 Timbrar fotos de reportes</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Estampa la <b>fecha/hora y la ubicación</b> sobre las fotos que suben los montadores.
              Si se desactiva, las fotos se suben sin timbrar.
            </div>
          </div>
          <Switch
            checked={!!draft.estampar_fotos}
            onCheckedChange={(v) => setDraft((d) => ({ ...d, estampar_fotos: v }))}
            data-testid="estampar-fotos-switch"
          />
        </div>
      </div>
      )}

      {/* Resumen diario */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-sm font-semibold text-zinc-100">📬 Resumen diario por email</div>
            <div className="text-xs text-zinc-500 mt-0.5">
              Acumula todas las notificaciones del día en <b>un único email</b> a la hora indicada.
              Útil si recibes muchas obras y no quieres un email por cada reporte.
            </div>
          </div>
          <Switch
            checked={!!draft.resumen_diario}
            onCheckedChange={(v) => setDraft((d) => ({ ...d, resumen_diario: v }))}
            data-testid="resumen-diario-switch"
          />
        </div>
        {draft.resumen_diario && (
          <div className="flex items-center gap-2 pt-2 border-t border-zinc-800">
            <span className="text-xs text-zinc-400">Hora de envío:</span>
            <Input type="time" value={draft.hora_resumen}
              onChange={(e) => setDraft((d) => ({ ...d, hora_resumen: e.target.value }))}
              className="w-24 h-8" data-testid="hora-resumen-input" />
            <span className="text-[10px] text-zinc-500">Por defecto 19:00. La hora aún no afecta al chron (corre a las 19:00 fijas).</span>
          </div>
        )}
      </div>

      {/* Eventos por canal */}
      <div className="rounded-lg border border-zinc-800 bg-zinc-950 overflow-hidden">
        <div className="grid grid-cols-12 gap-2 px-4 py-2 border-b border-zinc-800 bg-zinc-900/50 text-[10px] uppercase text-zinc-500 font-semibold">
          <div className="col-span-8">Evento</div>
          <div className="col-span-2 text-center"><Mail className="w-3 h-3 inline mr-1" /> Email</div>
          <div className="col-span-2 text-center"><MessageCircle className="w-3 h-3 inline mr-1" /> WhatsApp</div>
        </div>
        {events.map((ev) => {
          const c = draft.canales[ev.key] || { email: true, whatsapp: true };
          return (
            <div key={ev.key} className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-zinc-800/50 last:border-0 items-center"
              data-testid={`event-row-${ev.key}`}>
              <div className="col-span-8 text-sm text-zinc-200">{ev.label}</div>
              <div className="col-span-2 flex justify-center">
                <Switch checked={c.email} onCheckedChange={() => toggleCanal(ev.key, "email")}
                  data-testid={`switch-${ev.key}-email`} />
              </div>
              <div className="col-span-2 flex justify-center">
                <Switch checked={c.whatsapp} onCheckedChange={() => toggleCanal(ev.key, "whatsapp")}
                  data-testid={`switch-${ev.key}-whatsapp`} />
              </div>
            </div>
          );
        })}
      </div>

      {/* Nota explicativa */}
      {!isAdmin && (
        <div>
          <label className="text-xs text-zinc-400">Nota para DNF (opcional)</label>
          <Input value={note} onChange={(e) => setNote(e.target.value)}
            placeholder="Ej: prefiero solo WhatsApp porque me llegan muchos emails"
            className="mt-1" data-testid="nota-sugerencia" />
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between gap-2 pt-2">
        <div className="text-[10px] text-zinc-500">
          {data.prefs.updated_at && (
            <>Última actualización: {new Date(data.prefs.updated_at).toLocaleString("es-ES")} por <b>{data.prefs.updated_by || "?"}</b></>
          )}
        </div>
        <div className="flex gap-2">
          {onClose && <Button variant="outline" size="sm" onClick={onClose}>Cerrar</Button>}
          <Button onClick={save} disabled={saving}
            className={isAdmin ? "bg-emerald-500 text-zinc-950 hover:bg-emerald-400" : "bg-amber-500 text-zinc-950 hover:bg-amber-400"}
            data-testid="save-prefs-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> :
              (isAdmin ? <CheckCircle2 className="w-4 h-4 mr-2" /> : <Send className="w-4 h-4 mr-2" />)}
            {isAdmin ? "Guardar cambios" : "Enviar sugerencia a DNF"}
          </Button>
        </div>
      </div>
    </div>
  );
}
